import { useState } from 'react';
import { Phone, MessageSquare, X } from 'lucide-react';
import type { Agent } from '../types/cowork';
import { API_BASE_URL } from '../api/coworkApi';

function avatarSrc(agent: Agent) {
  if (!agent.avatarUrl) return undefined;
  if (agent.avatarUrl.startsWith('http')) return agent.avatarUrl;
  if (agent.avatarUrl.startsWith('/assets/')) return agent.avatarUrl;
  return `${API_BASE_URL}${agent.avatarUrl}`;
}

interface CallInvitePanelProps {
  agents: Agent[];
  initialSelected?: string[];
  onStart: (agentIds: string[]) => void;
  onClose: () => void;
  title?: string;
  actionLabel?: string;
  /** 'chat' swaps the phone icon for a chat bubble (text group chats). */
  actionIcon?: 'call' | 'chat';
}

export function CallInvitePanel({ agents, initialSelected = [], onStart, onClose, title, actionLabel, actionIcon = 'call' }: CallInvitePanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-[16px] text-[#101828] font-semibold">{title || 'Start a Call'}</h2>
            <p className="text-[12px] text-[#667085] mt-0.5">Select agents to invite</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4 text-[#667085]" />
          </button>
        </div>

        {/* Agent list */}
        <div className="px-4 py-3 max-h-[360px] overflow-y-auto">
          {agents.filter(a => a.isActive).map(agent => {
            const isSelected = selected.has(agent.id);
            return (
              <button
                key={agent.id}
                onClick={() => toggle(agent.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl mb-1 transition-all ${
                  isSelected
                    ? 'bg-[#f8f5ff] border border-[#be43ff]/20'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                {/* Avatar */}
                {avatarSrc(agent) ? (
                  <img src={avatarSrc(agent)} alt={agent.name} className="w-10 h-10 rounded-xl object-cover shrink-0" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${agent.gradientFrom}22, ${agent.gradientTo}22)`,
                      border: `1.5px solid ${agent.accentColor}30`,
                    }}
                  >
                    {agent.avatarEmoji}
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 text-left min-w-0">
                  <div className="text-[13px] text-[#101828] font-semibold">{agent.name}</div>
                  <div className="text-[11px] text-[#98a2b3] truncate">{agent.title}</div>
                </div>

                {/* Checkbox */}
                <div
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'border-[#be43ff] bg-[#be43ff]'
                      : 'border-gray-200'
                  }`}
                >
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[12px] text-[#98a2b3]">
            {selected.size} agent{selected.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => onStart(Array.from(selected))}
            disabled={selected.size === 0}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              selected.size > 0
                ? 'bg-[#be43ff] text-white hover:bg-[#a835e0] shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {actionIcon === 'chat' ? <MessageSquare className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
            {actionLabel || 'Start Call'}
          </button>
        </div>
      </div>
    </div>
  );
}
